import { modus } from '../api.js';
import { abschnitt, haengeAn, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { melde } from '../ergebnisse.js';
import { istAndroid, istIos } from '../geraet.js';
import type { Ansicht } from '../router.js';
import { lies, schreibe } from '../speicher.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

interface InstallAufforderung extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let aufforderung: InstallAufforderung | undefined;

/** Chrome am Android bietet die Installation per Ereignis an; früh abfangen, damit der Knopf es nutzen kann. */
export function fangeInstallAufforderung(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    aufforderung = e as InstallAufforderung;
  });
}

/** Meldet beim Start in der installierten App einmal das Ergebnis „installiert“. */
export async function pruefeInstallation(): Promise<void> {
  if (modus() !== 'app') return;
  const speicherAusBrowser = (await lies('speicherAusBrowser')) ?? false;
  const kennung = `app:${speicherAusBrowser}`;
  if ((await lies('installationGemeldet')) === kennung) return;
  await melde(
    'installation',
    'ok',
    speicherAusBrowser
      ? 'Läuft als installierte App. Die Anmeldung aus dem Browser war in der App schon vorhanden.'
      : 'Läuft als installierte App.',
    { speicherAusBrowser, displayMode: 'standalone' },
  );
  await schreibe('installationGemeldet', kennung);
}

export const installation: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const status = meldung();
  const speicherAusBrowser = (await lies('speicherAusBrowser')) ?? false;

  const anleitung = istIos()
    ? schritte(
        'Diese Seite in Safari öffnen (nicht in Chrome oder einer anderen App).',
        'Unten auf das Teilen-Symbol tippen (Quadrat mit Pfeil nach oben).',
        '„Zum Home-Bildschirm“ wählen, dann „Hinzufügen“.',
        'Die App über das neue Symbol „TFM Test“ öffnen und dort einrichten.',
      )
    : istAndroid()
      ? schritte('In Chrome oben rechts das Menü (drei Punkte) öffnen.', '„App installieren“ oder „Zum Startbildschirm hinzufügen“ wählen.', 'Die App über das neue Symbol öffnen.')
      : schritte('Am PC ist die Installation freiwillig.', 'In Chrome oder Edge in der Adressleiste das Installieren-Symbol wählen.');

  haengeAn(ziel, [
    ...kopf('Installation', 'Zuverlässig offline und mit Push läuft TFM nur als installierte App (Konzept, PWA und Web Push).'),
    abschnitt(
      'Stand',
      pruefpunktStand('installation', aufraeumer),
      liste(
        zeile('Läuft gerade als', modus() === 'app' ? 'installierte App' : 'Browser-Tab'),
        modus() === 'app' && zeile('Anmeldung aus dem Browser übernommen', speicherAusBrowser ? 'ja' : 'nein'),
      ),
    ),
    modus() === 'browser' &&
      abschnitt(
        'So wird installiert',
        anleitung,
        aufforderung &&
          knopf('Jetzt installieren', async () => {
            if (!aufforderung) return;
            await aufforderung.prompt();
            const wahl = await aufforderung.userChoice;
            setzeMeldung(status, wahl.outcome === 'accepted' ? 'Installiert. Jetzt die App über das neue Symbol öffnen.' : 'Installation abgebrochen.', wahl.outcome === 'accepted' ? 'ok' : 'info');
            aufforderung = undefined;
          }),
        status,
      ),
  ]);
  return aufraeumer.alles;
};
